import { z } from 'zod';
import { AppLocale } from './locale.js';
import { Banner } from './site.js';
import { ModuleKey } from './site.js';
/**
 * The admin API wire format (PLAN.md §7).
 *
 * Reads are wide, writes are narrow: settings accept any JSON value, secrets
 * accept a non-empty string and are never read back — the UI gets `last4` and
 * `updatedAt`, which is all a rotation workflow needs to confirm it took.
 */
export const AdminRole = z.enum(['viewer', 'editor', 'owner']);
export const AdminMe = z.object({
    email: z.string(),
    role: AdminRole,
    kind: z.enum(['user', 'service']),
    /** Which stack the console is pointed at, so staging is never mistaken for live. */
    environment: z.enum(['local', 'staging', 'production']).optional(),
    /** The API build answering this request, to compare against the console's own. */
    version: z.string().optional(),
    /** Public egress IP to whitelist in vendor firewalls; null until resolved. */
    serverIp: z.string().nullable().optional(),
});
export const AdminSettingEntry = z.object({
    key: z.string(),
    siteId: z.string().uuid().nullable(),
    value: z.unknown(),
    updatedBy: z.string().nullable(),
    updatedAt: z.string(),
});
export const AdminSecretEntry = z.object({
    key: z.string(),
    siteId: z.string().uuid().nullable(),
    /** All the admin plane ever sees of a secret. */
    last4: z.string().nullable(),
    keyVersion: z.number().int(),
    updatedBy: z.string().nullable(),
    updatedAt: z.string(),
});
export const AdminSetSettingRequest = z.object({
    value: z.unknown(),
    siteId: z.string().uuid().nullable().optional(),
});
export const AdminSetSecretRequest = z.object({
    value: z.string().min(1).max(8192),
    siteId: z.string().uuid().nullable().optional(),
});
/** A site's legal identity, the two documents the funnel links to, and the footer disclaimer (§9). */
export const AdminSetLegalRequest = z.object({
    entityName: z.string().trim().min(1, 'Enter the legal entity name.').max(200),
    cookiePolicyUrl: z.string().url('Enter a valid URL.').max(2048),
    privacyUrl: z.string().url('Enter a valid URL.').max(2048),
    disclaimer: z.string().max(8000).default(''),
});
/** The hero-slider banners for a site (§9). Order is the slide order. */
export const AdminSetBannersRequest = z.object({
    /** The English base, shown to any locale that has not localised its own. */
    banners: z.array(Banner).max(8),
    /**
     * Per-locale overrides. A locale omitted here follows the base; an empty
     * array means the same, so "follow English" and "custom but blank" collapse
     * to one rule.
     */
    bannersByLocale: z.record(AppLocale, z.array(Banner).max(8)).optional(),
});
/** Per-site analytics/tag HTML for the document head — Meta Pixel, GA, GTM (§19). */
export const AdminSetCustomHeadRequest = z.object({
    customHead: z.string().max(20000).default(''),
});
/**
 * A public hostname, strictly: at least two labels, no scheme, port, path or
 * trailing dot. This value lands in a unique index, in the ACME gate that
 * authorises certificate issuance, and in every absolute URL the API emits for
 * the site — so anything ambiguous is rejected here rather than normalised into
 * something the resolver will later fail to match.
 */
const HOSTNAME = /^(?=.{4,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;
export const SiteHost = z
    .string()
    .trim()
    .toLowerCase()
    .refine((h) => HOSTNAME.test(h), 'Enter a domain like onboarding.example.com — no https:// or path.');
/**
 * Adding a domain (§4.3). Only the two fields that cannot be inherited are
 * asked for; branding, locales and legal are copied from the primary site so a
 * new domain is a one-field operation rather than a form.
 */
export const AdminCreateSiteRequest = z.object({
    host: SiteHost,
    name: z.string().trim().min(1, 'Enter a display name.').max(120),
});
/** Renaming or switching a domain on/off. Nothing here can change its host. */
export const AdminUpdateSiteRequest = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    isActive: z.boolean().optional(),
});
/**
 * Turnstile's key pair for one domain. Both halves are required together: a
 * site key without its secret is a widget the server never verifies, which is
 * worse than no widget at all because it looks like protection (§18.2).
 */
export const AdminSetTurnstileRequest = z.object({
    siteKey: z.string().trim().min(1, 'Enter the Turnstile site key.').max(200),
    secret: z.string().trim().min(1, 'Enter the Turnstile secret key.').max(200),
});
/**
 * The funnel modules that are ON for a site (§7). The server enforces this list
 * — a module absent here does not render, its endpoint rejects, and its field
 * is not required. Order is irrelevant; duplicates are collapsed.
 */
export const AdminSetModulesRequest = z.object({
    modules: z
        .array(ModuleKey)
        .max(16)
        .transform((keys) => [...new Set(keys)]),
});
export const AdminUpsertAdminRequest = z.object({
    email: z.string().email().or(z.string().min(1).max(320)),
    role: AdminRole,
});
/** Finding one application by the address the applicant signed up with. */
export const AdminLookupApplicationRequest = z.object({
    email: z.string().trim().toLowerCase().email('Enter the email address to look up.').max(320),
});
export const AdminAuditEntry = z.object({
    id: z.string().uuid(),
    actor: z.string(),
    action: z.string(),
    subject: z.string(),
    siteId: z.string().uuid().nullable(),
    before: z.unknown().nullable(),
    after: z.unknown().nullable(),
    ip: z.string().nullable(),
    at: z.string(),
});
//# sourceMappingURL=admin.js.map